import React, { useState } from 'react';
import './LoginModal.css';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (username: string, password: string) => Promise<void>;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await onLogin(username, password);
      setUsername('');
      setPassword('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setUsername('');
    setPassword('');
    setError('');
    onClose();
  };

  const fillTestUser = (user: 'admin' | 'user' | 'testuser') => {
    const credentials = {
      admin: { username: 'admin', password: 'admin123' },
      user: { username: 'user', password: 'user123' },
      testuser: { username: 'testuser', password: 'test123' },
    };
    setUsername(credentials[user].username);
    setPassword(credentials[user].password);
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" role="presentation" onClick={handleClose}>
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="login-modal-title">Login to API V2</h2>
          <button className="modal-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              required
              autoComplete="username"
              autoFocus
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
              autoComplete="current-password"
              disabled={isLoading}
            />
          </div>

          <div className="test-users">
            <p className="test-users-title">Test Users (click to fill):</p>
            <div className="test-users-buttons">
              <button
                type="button"
                onClick={() => fillTestUser('admin')}
                className="test-user-btn"
                disabled={isLoading}
              >
                admin/admin123 (ADMIN)
              </button>
              <button
                type="button"
                onClick={() => fillTestUser('user')}
                className="test-user-btn"
                disabled={isLoading}
              >
                user/user123 (USER)
              </button>
              <button
                type="button"
                onClick={() => fillTestUser('testuser')}
                className="test-user-btn"
                disabled={isLoading}
              >
                testuser/test123 (USER)
              </button>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={handleClose} className="btn-secondary" disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginModal;
